import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Database, Sparkles, MessageSquare, ArrowRight, Building2, Package, Plug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConnectSourceDialog } from "@/components/ConnectSourceDialog";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { AUTH } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

const SECTORS = ["SaaS", "Healthcare", "FinServ", "Retail", "Logistics", "Manufacturing", "Other"];

const SAMPLE_QUESTIONS = {
  SaaS: ["What was revenue last month?", "Which facilities have the most residents?", "Show occupancy trend by month"],
  Healthcare: ["How many residents are in each facility?", "What is the average length of stay?", "Show monthly admissions trend"],
  FinServ: ["What is total billing by facility?", "Show outstanding balances by month", "Top 10 residents by charges"],
  Retail: ["What were charges by service category?", "Which facility beat its target?", "Show monthly revenue trend"],
  Logistics: ["Show admissions by month", "Which facility has the highest occupancy?", "Average length of stay by facility"],
  Manufacturing: ["Show revenue by facility", "Which facility has the most residents?", "Monthly occupancy trend"],
  Other: ["How many residents per facility?", "Show revenue by month", "What is the average length of stay?"],
};

const STEP_META = [
  { key: "sector", label: "Pick your sector", icon: Building2 },
  { key: "source", label: "Connect data", icon: Database },
  { key: "discovery", label: "Run discovery", icon: Sparkles },
  { key: "first_question", label: "Ask a question", icon: MessageSquare },
];

export default function Onboarding() {
  const { active } = useWorkspace();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pack, setPack] = useState(null);
  const [discovering, setDiscovering] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    if (!active) return;
    const { data } = await api.get(`/workspaces/${active.id}/onboarding`);
    setState(data);
    if (data.completed) navigate("/dashboard", { replace: true });
  }, [active, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const patch = async (body) => {
    const { data } = await api.patch(`/workspaces/${active.id}/onboarding`, body);
    setState(data);
    return data;
  };

  const pickSector = async (sector) => {
    setBusy(true);
    try {
      const data = await patch({ sector });
      if (data.installed_pack) {
        setPack(data.installed_pack);
        const a = data.installed_pack.added || {};
        toast.success(`${data.installed_pack.name} pack loaded — ${a.glossary || 0} terms, ${a.metrics || 0} metrics added to your glossary.`);
      }
    } finally { setBusy(false); }
  };

  const pollDiscovery = (jobId) => {
    let tries = 0;
    setDiscovering(true);
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        if (data.status === "completed" || tries > 30) {
          clearInterval(pollRef.current);
          setDiscovering(false);
          await patch({ step: "discovery" });
        }
      } catch {
        clearInterval(pollRef.current);
        setDiscovering(false);
        await patch({ step: "discovery" });
      }
    }, 2000);
  };

  const loadDemo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sources/seed-sample", {}, { params: { workspace_id: active.id } });
      await patch({ step: "source" });
      toast.success("Demo warehouse loaded — running discovery…");
      pollDiscovery(data.glossary_job_id || data.introspection_job_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load demo data");
    } finally {
      setBusy(false);
    }
  };

  const onOwnSourceConnected = async () => {
    await patch({ step: "source" });
    await patch({ step: "discovery" });
  };

  const askQuestion = async (q) => {
    await patch({ step: "first_question" });
    navigate(`/chat?q=${encodeURIComponent(q)}`);
  };

  if (!state) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-accent" />
      </div>
    );
  }

  const steps = state.steps;
  const sector = state.sector || "Other";
  const questions = SAMPLE_QUESTIONS[sector] || SAMPLE_QUESTIONS.Other;

  return (
    <div data-testid={AUTH.onboardingRoot} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-3xl px-6 py-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Let's get you to your first answer.</h1>
            <p className="mt-2 text-sm text-muted-foreground">Four quick steps — under ten minutes.</p>
          </div>
          <button data-testid={AUTH.onboardingSkip} onClick={() => navigate("/dashboard")} className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground">
            Skip for now
          </button>
        </div>

        {/* checklist */}
        <ol className="mt-8 flex flex-wrap gap-2">
          {STEP_META.map((s, i) => {
            const done = steps[s.key];
            const Icon = done ? Check : s.icon;
            return (
              <li key={s.key} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] ${done ? "border-accent/50 bg-accent/10 text-accent" : "border-border/60 text-muted-foreground"}`}>
                <Icon className="h-3.5 w-3.5" />
                {i + 1}. {s.label}
              </li>
            );
          })}
        </ol>

        {/* Step 1 — sector */}
        <Card active={!steps.sector} done={steps.sector} title="1. What sector are you in?" subtitle="We load a sector vocabulary so Sema speaks your language from minute one.">
          <div data-testid={AUTH.onboardingSector} className="flex flex-wrap gap-2">
            {SECTORS.map((s) => (
              <button
                key={s}
                disabled={busy}
                onClick={() => pickSector(s)}
                data-testid={`onboarding-sector-${s.toLowerCase()}`}
                className={`rounded-full border px-4 py-2 text-xs uppercase tracking-[0.12em] transition-colors ${state.sector === s ? "border-accent bg-accent/15 text-accent" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
              >
                {s}
              </button>
            ))}
          </div>
          {pack && (
            <div data-testid="onboarding-pack-loaded" className="mt-4 flex items-start gap-3 rounded-xl border border-accent/40 bg-accent/5 px-4 py-3">
              <Package className="mt-0.5 h-4 w-4 text-accent" />
              <div className="text-xs">
                <p className="font-semibold text-foreground">{pack.name} semantic pack loaded</p>
                <p className="mt-0.5 text-muted-foreground">
                  Added {pack.added?.glossary || 0} draft glossary terms and {pack.added?.metrics || 0} metrics
                  ({pack.contributions?.joins || 0} join + {pack.contributions?.alerts || 0} alert templates).
                  Promote them anytime from your <span className="text-accent">Glossary</span> and <span className="text-accent">Metrics</span>.
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Step 2 — connect */}
        <Card active={steps.sector && !steps.source} done={steps.source} title="2. Connect a data source" subtitle="Bring your own warehouse, or explore Sema on sample data.">
          <div className="flex flex-wrap gap-3">
            <Button onClick={loadDemo} disabled={busy} data-testid={AUTH.onboardingLoadDemo} className="rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Database className="mr-2 h-4 w-4" />Skip & explore demo data</>}
            </Button>
            <ConnectSourceDialog
              workspaceId={active?.id}
              onConnected={onOwnSourceConnected}
              trigger={
                <Button variant="outline" data-testid={AUTH.onboardingConnectOwn} className="rounded-full border-border/80 text-xs uppercase tracking-[0.16em]">
                  <Plug className="mr-2 h-4 w-4" /> Connect your own
                </Button>
              }
            />
          </div>
        </Card>

        {/* Step 3 — discovery */}
        <Card active={steps.source && !steps.discovery} done={steps.discovery} title="3. Run discovery" subtitle="Sema profiles your schema and generates a business glossary.">
          {discovering ? (
            <p className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin text-accent" />Profiling schema and generating glossary…</p>
          ) : steps.discovery ? (
            <p className="text-sm text-accent">Discovery complete — your semantic layer is ready.</p>
          ) : (
            <p className="text-sm text-muted-foreground">Connect a source above to kick off discovery.</p>
          )}
        </Card>

        {/* Step 4 — first question */}
        <Card active={steps.discovery && !steps.first_question} done={steps.first_question} title="4. Ask your first question" subtitle={`Sector picks for ${sector}. Click one to open chat.`}>
          <div className="flex flex-col gap-2">
            {questions.map((q) => (
              <button
                key={q}
                onClick={() => askQuestion(q)}
                data-testid={AUTH.onboardingSampleQuestion}
                className="group inline-flex items-center justify-between rounded-lg border border-border/60 bg-card/40 px-4 py-3 text-left text-sm transition-colors hover:border-accent/50 hover:bg-accent/5"
              >
                <span>{q}</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-accent" />
              </button>
            ))}
          </div>
        </Card>

        <div className="mt-8 flex justify-end">
          <Button onClick={() => navigate("/dashboard")} data-testid={AUTH.onboardingFinish} variant="outline" className="rounded-full border-border/80 text-xs uppercase tracking-[0.18em]">
            Go to dashboard
          </Button>
        </div>
      </main>
    </div>
  );
}

function Card({ active, done, title, subtitle, children }) {
  return (
    <section className={`mt-5 rounded-xl border p-5 transition-colors ${active ? "border-accent/40 bg-card/60" : done ? "border-border/40 bg-card/30 opacity-80" : "border-border/50 bg-card/30"}`}>
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full border text-[11px] ${done ? "border-accent/60 bg-accent/15 text-accent" : "border-border/60 text-muted-foreground"}`}>
          {done ? <Check className="h-3.5 w-3.5" /> : null}
        </span>
        <div className="flex-1">
          <h2 className="text-base font-semibold tracking-tight">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </section>
  );
}
